import React, { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { GoalItem, GoalsGroup, MANUAL_GOALS_GROUP } from "@/types/goals";
import { PabSession, api } from "@/services/api";
import { useToast } from "@/hooks/useToast";
import { GoalSelectionCard } from "./GoalSelectionCard";
import { AddGoalCard } from "./AddGoalCard";
import { AILoadingAnimation } from "@/components/AILoadingAnimation";
import { MAX_SELECTED_GOALS } from "../ConversationalGoalSetting";

interface GoalsMessageProps {
  pabSession: PabSession | undefined;
  selectedGoals: GoalItem[];
  handleSetSelectedGoal: (goal: GoalItem, isSelected: boolean) => void;
  handleConfirmMyGoals: () => void;
  disabled?: boolean;
  onContentLoaded?: () => void;
}

const GoalsMessage: React.FC<GoalsMessageProps> = ({
  pabSession,
  selectedGoals,
  handleSetSelectedGoal,
  handleConfirmMyGoals,
  disabled,
  onContentLoaded,
}) => {
  const [goalGroups, setGoalGroups] = useState<GoalsGroup[]>([]);
  const [fetching, setFetching] = useState(true);
  const [editingGoalId, setEditingGoalId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingDetail, setEditingDetail] = useState("");
  const { showToast } = useToast();

  useEffect(() => {
    if (!pabSession?.id) return;
    api.pabSessions
      .getOkrAiRecommendations(pabSession.id, false)
      .then((data) => {
        setGoalGroups(data);
        setFetching(false);
        onContentLoaded?.();
      })
      .catch(() => {
        setGoalGroups([]);
        setFetching(false);
      });
  }, [pabSession?.id, onContentLoaded]);

  const handleGoalToggle = (goal: GoalItem) => {
    const isCurrentlySelected = selectedGoals.some((g) => g.id === goal.id);
    if (!isCurrentlySelected && selectedGoals.length >= MAX_SELECTED_GOALS) return;
    handleSetSelectedGoal(goal, !isCurrentlySelected);
  };

  const handleEditStart = (goalId: number, currentTitle: string, currentDetail?: string) => {
    setEditingGoalId(goalId);
    setEditingText(currentTitle);
    setEditingDetail(currentDetail ?? "");
  };

  const handleEditSave = (groupIndex: number, goalIndex: number) => {
    if (!editingText.trim()) return;
    const updatedGroups = [...goalGroups];
    const oldGoal = updatedGroups[groupIndex].goals[goalIndex];
    const updatedGoal = {
      ...oldGoal,
      title: editingText.trim(),
      description: editingDetail.trim(),
    };
    updatedGroups[groupIndex].goals[goalIndex] = updatedGoal;
    setGoalGroups(updatedGroups);

    const selectedIndex = selectedGoals.findIndex((g) => g.id === oldGoal.id);
    if (selectedIndex !== -1) {
      handleSetSelectedGoal(oldGoal, false);
      handleSetSelectedGoal(updatedGoal, true);
    }
    setEditingGoalId(null);
    setEditingText("");
    setEditingDetail("");
  };

  const handleEditCancel = () => {
    setEditingGoalId(null);
    setEditingText("");
    setEditingDetail("");
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    groupIndex: number,
    goalIndex: number,
  ) => {
    if (e.key === "Enter") handleEditSave(groupIndex, goalIndex);
    else if (e.key === "Escape") handleEditCancel();
  };

  // The achiever's own goal: saved straight away so it has a real id, filed
  // under "Your Goals", and ticked if there is room — they wrote it because
  // they want it.
  const handleAddGoal = async (goal: { title: string; description: string }) => {
    if (!pabSession?.id) return false;
    try {
      const created = await api.pabSessions.addManualOkr(pabSession.id, goal);
      setGoalGroups((groups) => {
        const idx = groups.findIndex((g) => g.group === MANUAL_GOALS_GROUP);
        if (idx === -1) {
          return [
            ...groups,
            { id: -1, group: MANUAL_GOALS_GROUP, description: "", goals: [created] },
          ];
        }
        return groups.map((g, i) => (i === idx ? { ...g, goals: [...g.goals, created] } : g));
      });
      if (selectedGoals.length < MAX_SELECTED_GOALS) handleSetSelectedGoal(created, true);
      return true;
    } catch {
      showToast({ type: "error", message: "Couldn't add your goal. Please try again." });
      return false;
    }
  };

  const canContinue = !disabled && selectedGoals.length >= 3 && !fetching;

  // Flatten all goals into a single list with their category
  const flatGoals = goalGroups.flatMap((group, groupIndex) =>
    group.goals.map((goal, goalIndex) => ({ goal, group, groupIndex, goalIndex })),
  );

  if (fetching) {
    return (
      <div className="bg-white dark:bg-surface-form-card rounded-2xl border border-secondary-200 dark:border-surface-form-border shadow-card p-10 flex items-center justify-center min-h-[300px]">
        <AILoadingAnimation
          variant="purple"
          message="Generating your personalized goals..."
          className=""
        />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-surface-form-card rounded-2xl border border-secondary-200 dark:border-surface-form-border shadow-card p-10 flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-4xl font-bold text-secondary-900 dark:text-white">Select Your Goals</h1>
        <p className="text-lg text-accent-250 dark:text-pab-dark-subtle-text">
          Choose 3-{MAX_SELECTED_GOALS} goals that resonate most with your career aspirations
        </p>
        <p className="text-sm text-accent-250 dark:text-pab-dark-subtle-text">
          Selected: {selectedGoals.length}/{MAX_SELECTED_GOALS} goals
        </p>
      </div>

      {/* Goal cards — flat vertical list */}
      <div className="flex flex-col gap-4">
        {flatGoals.map(({ goal, group, groupIndex, goalIndex }) => {
          const isSelected = selectedGoals.some((g) => g.id === goal.id);
          const isDisabled = !isSelected && selectedGoals.length >= MAX_SELECTED_GOALS;
          const isEditing = editingGoalId === goal.id;

          return (
            <GoalSelectionCard
              key={goal.id}
              id={`goal-${goal.id}`}
              title={goal.title}
              category={group.group}
              detail={goal.description}
              isSelected={isSelected}
              isDisabled={isDisabled}
              isEditing={isEditing}
              editingText={editingText}
              editingDetail={editingDetail}
              onToggle={() => handleGoalToggle(goal)}
              onEditStart={() => handleEditStart(goal.id, goal.title, goal.description)}
              onEditSave={() => handleEditSave(groupIndex, goalIndex)}
              onEditCancel={handleEditCancel}
              onEditChange={setEditingText}
              onEditDetailChange={setEditingDetail}
              onKeyDown={(e) => handleKeyDown(e, groupIndex, goalIndex)}
            />
          );
        })}

        {!disabled && <AddGoalCard onAdd={handleAddGoal} />}
      </div>

      {/* CTA */}
      <button onClick={handleConfirmMyGoals} disabled={!canContinue} className="btn-gradient-cta">
        Confirm My Goals ({selectedGoals.length} goal{selectedGoals.length !== 1 ? "s" : ""}{" "}
        selected)
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

export default React.memo(GoalsMessage);
